import { useQueryClient, type UseQueryOptions } from 'react-query'

import {
  apiPaths,
  handleError,
  queryKeys,
  validateStatus,
} from 'api/kinerja/pengawas-sekolah/config'
import { useQueryApi, useMutationApi } from 'utils/api'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { AxiosError } from 'axios'
import type { CommonResponse } from 'types/api'
import type {
  TPraktikKinerjaItem,
  TSatuanPendidikanItem,
  TRHKOptionType,
  ResponseTPerencanaanSummary,
  TPerencanaanSubmissionResponse,
  TPerencanaan,
  TPengembanganKompetensiIndikatorOptions,
  TPengembanganKompetensiKategori,
  TPengembanganKompetensiKegiatan,
  TPerencanaanRequestBody,
  TPerencanaanPerilakuKerjaRequestBody,
  TMulaiPelaksanaanRequestBody,
  TResetSKPPhase,
  TSatuanPendidikanV2Item,
} from 'types/kinerja-ps-pegawai'

// Praktik Kinerja Options
export function useAPIGetPerencanaanRHKOptions({
  type,
  opts,
}: {
  type?: TRHKOptionType
  opts?: UseQueryOptions<CommonResponse<TPraktikKinerjaItem[]>>
}) {
  return useQueryApi<CommonResponse<TPraktikKinerjaItem[]>>(
    `${apiPaths.perencanaan}/rhk-ps/${type}`,
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [queryKeys.API_GET_PRAKTIK_KINERJA_OPTIONS, type],
    }
  )
}

export function useAPIGetPerencanaanSatuanPendidikanOptions({
  periodeId,
  rhkId,
  opts,
}: {
  periodeId?: string
  rhkId?: string
  opts?: UseQueryOptions<CommonResponse<TSatuanPendidikanItem[]>>
}) {
  return useQueryApi<CommonResponse<TSatuanPendidikanItem[]>>(
    `${apiPaths.perencanaan}/${periodeId}/rapor-satuan-pendidikan/${rhkId}`,
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [queryKeys.API_GET_SATUAN_PENDIDIKAN_OPTIONS, periodeId, rhkId],
    }
  )
}

export function useAPIGetPerencanaanSatuanPendidikanV2Options({
  periodeId,
  rhkIds,
  opts,
}: {
  periodeId?: string
  rhkIds?: string[]
  opts?: UseQueryOptions<CommonResponse<TSatuanPendidikanV2Item[]>>
}) {
  return useQueryApi<CommonResponse<TSatuanPendidikanV2Item[]>>(
    `${apiPaths.perencanaanV2}/${periodeId}/rapor-satuan-pendidikan`,
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [
        queryKeys.API_GET_SATUAN_PENDIDIKAN_OPTIONS,
        periodeId,
        ...(rhkIds || []),
      ],
      axiosConfig: {
        params: {
          rhkIds,
        },
      },
    }
  )
}

// Pengembangan Kompetensi Options
export function useAPIGetIndikatorKompetensiOptions() {
  return useQueryApi<TPengembanganKompetensiIndikatorOptions>(
    `${apiPaths.pengembanganKompetensi}/indikator`,
    {
      queryConfig: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [queryKeys.API_GET_PENGEMBANGAN_KOMPETENSI_INDIKATOR],
    }
  )
}
export function useAPIGetKompetensiKategoriOptions() {
  return useQueryApi<CommonResponse<TPengembanganKompetensiKategori[]>>(
    `${apiPaths.pengembanganKompetensi}/kategori`,
    {
      queryConfig: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [queryKeys.API_GET_PENGEMBANGAN_KOMPETENSI_KATEGORI],
    }
  )
}
export function useAPIGetKompetensiKegiatanOptions({
  kategoriId,
  ...opts
}: UseQueryOptions<CommonResponse<TPengembanganKompetensiKegiatan[]>> & {
  kategoriId?: string
}) {
  const { period } = usePeriodContext()
  return useQueryApi<CommonResponse<TPengembanganKompetensiKegiatan[]>>(
    `${apiPaths.pengembanganKompetensiByPeriod(
      period.id
    )}/kategori/${kategoriId}/kegiatan`,
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [
        queryKeys.API_GET_PENGEMBANGAN_KOMPETENSI_KEGIATAN,
        kategoriId,
        period.id,
      ],
    }
  )
}

export function useAPIGetPerencanaanSummary({
  periodId,
  ...options
}: UseQueryOptions<ResponseTPerencanaanSummary> & { periodId: string }) {
  return useQueryApi<ResponseTPerencanaanSummary>(
    apiPaths.summaryPerencanaan(periodId),
    {
      queryKey: [queryKeys.API_GET_PERENCANAAN_SUMMARY, periodId],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPerencanaan({
  periodId,
  ...options
}: UseQueryOptions<TPerencanaan> & { periodId: string }) {
  return useQueryApi<TPerencanaan>(apiPaths.perencanaanByPeriod(periodId), {
    queryKey: [queryKeys.API_GET_PERENCANAAN, periodId],
    axiosConfig: {
      validateStatus,
    },
    queryConfig: {
      useErrorBoundary: true,
      ...options,
    },
    errorHandlerFn: handleError,
  })
}

export function useAPISubmitPerencanaan<T>(apiPath: string) {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<TPerencanaanSubmissionResponse, AxiosError, T>(
    apiPath,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_PERENCANAAN_SUMMARY,
            period.id,
          ])
        },
      },
    }
  )
}

export function useAPISubmitPerilakuKerja() {
  return useAPISubmitPerencanaan<TPerencanaanPerilakuKerjaRequestBody>(
    `${apiPaths.perencanaan}/perilaku-kerja`
  )
}

export function useAPIAjukanPerencanaan(periodId: string) {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<
    TPerencanaanSubmissionResponse,
    AxiosError,
    TPerencanaanRequestBody
  >(() => apiPaths.ajukanPerencanaan(periodId), {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PERENCANAAN_SUMMARY,
          period.id,
        ])
      },
    },
  })
}

export function useAPIMulaiPelaksanaan() {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<
    TPerencanaanSubmissionResponse,
    AxiosError,
    TMulaiPelaksanaanRequestBody
  >(() => apiPaths.mulaiPelaksanaan, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PERENCANAAN_SUMMARY,
          period.id,
        ])
      },
    },
  })
}

export function useApiResetSkp({ phase }: { phase: TResetSKPPhase }) {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<{ message: string }, AxiosError, {}>(
    () => apiPaths.resetSkp(period.id, phase),
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_PERENCANAAN,
            period.id,
          ])

          queryClient.invalidateQueries([
            queryKeys.API_GET_PERENCANAAN_SUMMARY,
            period.id,
          ])
        },
      },
    }
  )
}
